import {
  ShoppingCart,
  UserSquare,
  ClipboardList,
  CreditCard,
  ReceiptText,
  Truck,
  Undo2,
  RotateCcw,
  BookMarked,
  PackageCheck,
  BrainCircuit,
} from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
  LabelList,
} from 'recharts';
import { Link } from 'react-router-dom';
import {
  Lead,
  P,
  H3,
  UL,
  LI,
  Code,
  Callout,
  Steps,
  Step,
  Flow,
  Lifecycle,
  DefList,
  Matrix,
  Checklist,
  ChartCard,
} from './components';
import type { GuideChapter } from './tracks';

const ORANGE = 'hsl(22 100% 55%)';
const TEAL = 'hsl(188 75% 32%)';
const GRAY = 'hsl(220 9% 62%)';

function GoTo({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-1.5 py-0.5 text-[12px] font-medium text-primary ring-1 ring-inset ring-primary/20 hover:bg-primary/15"
    >
      {children}
    </Link>
  );
}

// ── 1 · Overview ───────────────────────────────────────────────────────────
function Overview() {
  return (
    <div>
      <Lead>
        Selling a finished good runs as one continuous pipeline: a customer places an{' '}
        <strong className="text-foreground">order</strong>, it’s <strong className="text-foreground">paid</strong>,
        an <strong className="text-foreground">invoice</strong> is raised, it’s{' '}
        <strong className="text-foreground">dispatched</strong> on the promised courier, and{' '}
        <strong className="text-foreground">delivered</strong> — with returns, cancellations and refunds
        handled cleanly afterwards.
      </Lead>

      <H3>The end-to-end flow</H3>
      <Flow
        steps={[
          { icon: UserSquare, title: 'Customer', sub: 'who is buying', tone: 'muted' },
          { icon: ShoppingCart, title: 'Sales order', sub: 'what they want', tone: 'accent' },
          { icon: CreditCard, title: 'Payment', sub: 'prepaid / COD' },
          { icon: ReceiptText, title: 'Invoice', sub: 'auto on payment', tone: 'success' },
          { icon: Truck, title: 'Dispatch', sub: 'on the promised courier' },
          { icon: PackageCheck, title: 'Delivered', sub: 'tracked to the door', tone: 'success' },
          { icon: Undo2, title: 'After-sales', sub: 'returns · refunds', tone: 'muted' },
        ]}
      />

      <H3>Where it lives</H3>
      <DefList
        items={[
          { term: 'Fulfilment', def: <><GoTo to="/customers">Customers</GoTo>, <GoTo to="/orders">Sales Orders</GoTo>, <GoTo to="/invoices">Invoices</GoTo></> },
          { term: 'Delivery', def: <><GoTo to="/dispatch/dispatcher">Dispatcher</GoTo> — one control plane for carriers, labels, tracking</> },
          { term: 'Post-sales', def: <><GoTo to="/cancel-requests">Cancel Requests</GoTo>, <GoTo to="/return-requests">Return Requests</GoTo>, <GoTo to="/returns">Return Orders</GoTo>, <GoTo to="/refunds">Refunds</GoTo>, <GoTo to="/my-reviews">My Reviews</GoTo></> },
        ]}
      />

      <H3>Two ways orders arrive</H3>
      <UL>
        <LI><strong>From your portal</strong> — the storefront creates the order via the API, with the customer, address and the delivery option they chose already attached (see the Portal Integration guide).</LI>
        <LI><strong>Manually</strong> — you raise an order yourself in <GoTo to="/orders">Sales Orders</GoTo> for phone/offline sales.</LI>
      </UL>

      <ChartCard title="Order pipeline" hint="illustrative — your numbers live in Reports">
        <FunnelChart />
      </ChartCard>
    </div>
  );
}

// ── 2 · Customers ────────────────────────────────────────────────────────────
function Customers() {
  return (
    <div>
      <Lead>
        Every order belongs to a <strong className="text-foreground">customer</strong>. The customer
        record holds who they are and where they’re billed and shipped — and it carries the GSTIN that
        decides how their invoices are taxed.
      </Lead>

      <P>
        Manage them under <GoTo to="/customers">Customers</GoTo>. Portal orders auto-create the
        customer; for manual orders you add them here first.
      </P>

      <DefList
        items={[
          { term: 'Code & name', def: 'A unique code within your workspace plus the business/legal name.' },
          { term: 'Contact', def: 'Email and phone.' },
          { term: 'GSTIN', def: <>Their GST number (15 chars). Its state code drives CGST+SGST vs IGST on their invoices.</> },
          { term: 'Addresses', def: <>Unlimited, each tagged <Code>BILLING</Code>, <Code>SHIPPING</Code> or <Code>BOTH</Code>, with a per-address GSTIN that overrides the customer’s when present.</> },
          { term: 'Status', def: <><Code>ACTIVE</Code> or archived (<Code>INACTIVE</Code>).</> },
        ]}
      />

      <Callout tone="tip" title="Address GSTIN wins">
        If a shipping address has its own GSTIN (e.g. a branch in another state), that one is used for
        tax — so inter-state orders compute IGST correctly even for the same customer.
      </Callout>
    </div>
  );
}

// ── 3 · Sales orders ─────────────────────────────────────────────────────────
function Orders() {
  return (
    <div>
      <Lead>
        A <strong className="text-foreground">sales order</strong> is the commitment to sell. It carries
        the customer, the line items, the delivery promise and the money — and moves through a clear
        lifecycle from placed to delivered.
      </Lead>

      <H3>What an order holds</H3>
      <DefList
        items={[
          { term: 'Customer & address', def: 'Who’s buying and where it ships.' },
          { term: 'Line items', def: 'SKU, quantity and unit price; line and order totals roll up with tax.' },
          { term: 'Payment mode', def: <><Code>PREPAID</Code> (default) or <Code>COD</Code>.</> },
          { term: 'Delivery commitment', def: <>The chosen tier (Standard/Express), the committed courier and the promised delivery date (EDD) — frozen at checkout.</> },
          { term: 'Money', def: <>Total, shipping, discount, additional charges, round-off and the <Code>grandTotal</Code>.</> },
        ]}
      />

      <H3>The order lifecycle</H3>
      <Lifecycle
        states={[
          { label: 'Draft', tone: 'muted' },
          { label: 'Order placed', tone: 'default' },
          { label: 'Stock reserved', tone: 'default' },
          { label: 'Payment pending', tone: 'warn' },
          { label: 'Payment confirmed', tone: 'accent' },
          { label: 'Ready to dispatch', tone: 'default' },
          { label: 'Dispatched', tone: 'default' },
          { label: 'Delivered', tone: 'success' },
        ]}
        branches={[{ from: 'any active state', to: [{ label: 'Cancelled', tone: 'danger' }] }]}
      />

      <P>
        To raise one manually, open <GoTo to="/orders">Sales Orders</GoTo> → <strong>New order</strong>,
        pick the customer and warehouse, add line items, and set any shipping/discount charges. Stock is
        reserved as the order is confirmed.
      </P>

      <Callout tone="info" title="Prepaid vs COD changes everything downstream">
        A <strong>PREPAID</strong> order must be paid before you can invoice or dispatch it. A{' '}
        <strong>COD</strong> order skips that gate — the courier collects the grand total on delivery.
        The next chapter covers exactly what each gate allows.
      </Callout>
    </div>
  );
}

// ── 4 · Payment & gating ─────────────────────────────────────────────────────
function Payments() {
  return (
    <div>
      <Lead>
        Payment is the control point of the whole sell-side. The rules live in one place, so a prepaid
        order can never be invoiced or shipped before the money is in — every screen and the portal
        obey the same gate.
      </Lead>

      <H3>Recording payment</H3>
      <P>
        Prepaid payments usually land from the portal’s gateway; you can also record one against the
        order. Payment moves through <Code>PENDING</Code> → <Code>INITIATED</Code> →{' '}
        <Code>SUCCESS</Code> (or <Code>FAILED</Code>). On <strong>SUCCESS</strong> the order becomes{' '}
        <strong>Payment confirmed</strong> and two things happen automatically.
      </P>
      <Flow
        steps={[
          { icon: CreditCard, title: 'Payment SUCCESS', sub: 'gateway confirms', tone: 'accent' },
          { icon: ReceiptText, title: 'Invoice raised', sub: 'auto, best-effort', tone: 'success' },
          { icon: Truck, title: 'Dispatch drafted', sub: 'order + label created', tone: 'success' },
        ]}
      />

      <H3>What each gate allows</H3>
      <Matrix
        columns={['Prepaid · unpaid', 'Prepaid · paid', 'COD']}
        rows={[
          { label: 'Generate invoice', cells: [false, true, true] },
          { label: 'Mark ready for dispatch', cells: [false, true, true] },
          { label: 'Mark dispatched', cells: [false, true, true] },
        ]}
      />
      <UL>
        <LI><strong>Prepaid, unpaid</strong> — invoicing and dispatch are blocked until payment succeeds.</LI>
        <LI><strong>Prepaid, paid</strong> — fully unlocked; invoice auto-raised, dispatch drafted.</LI>
        <LI><strong>COD</strong> — treated as settled, so it unlocks once stock is reserved; the courier collects on delivery.</LI>
      </UL>

      <Callout tone="warn" title="The gate is enforced everywhere">
        These rules are centralised, so they hold whether an action comes from the ERP UI or the portal
        API. You can’t accidentally ship an unpaid prepaid order from any surface.
      </Callout>
    </div>
  );
}

// ── 5 · Invoicing ────────────────────────────────────────────────────────────
function Invoicing() {
  return (
    <div>
      <Lead>
        Invoices are your GST tax documents. Most are raised automatically the moment payment succeeds;
        you can also generate one by hand when the gate allows.
      </Lead>

      <H3>How invoices are raised</H3>
      <UL>
        <LI><strong>Automatic</strong> — on payment <Code>SUCCESS</Code>, the invoice is generated best-effort (a missing billing address is logged, never blocks payment).</LI>
        <LI><strong>Manual</strong> — click <strong>Generate invoice</strong> on a settled order in <GoTo to="/invoices">Invoices</GoTo> or on the order itself.</LI>
      </UL>

      <H3>GST split</H3>
      <P>The invoice computes tax from the place of supply vs your state, from the GSTIN:</P>
      <DefList
        items={[
          { term: 'Intra-state', def: <>Same state → the rate splits into <Code>CGST</Code> + <Code>SGST</Code> (shown as “CGST+SGST”).</> },
          { term: 'Inter-state', def: <>Different state, incl. exports → the full rate is <Code>IGST</Code>.</> },
        ]}
      />

      <H3>Statuses & actions</H3>
      <Lifecycle
        states={[
          { label: 'DRAFT', tone: 'muted' },
          { label: 'ISSUED', tone: 'accent' },
          { label: 'PAID', tone: 'success' },
        ]}
        branches={[
          { from: 'ISSUED', to: [{ label: 'PARTIALLY_PAID', tone: 'warn' }, { label: 'CANCELLED', tone: 'danger' }] },
        ]}
      />
      <UL>
        <LI><strong>Print invoice</strong> — a clean GST invoice you can save or send.</LI>
        <LI><strong>Cancel invoice</strong> — with a reason, for the audit trail (e.g. to re-issue with corrected details).</LI>
      </UL>

      <Callout tone="tip" title="Numbering is automatic">
        Invoice numbers follow the scheme you set in your organization profile (prefix + period +
        counter) — see the “Set up your organization” guide.
      </Callout>
    </div>
  );
}

// ── 6 · Dispatch & delivery ──────────────────────────────────────────────────
function Dispatch() {
  return (
    <div>
      <Lead>
        Once an order is paid (or COD), it’s ready to ship. The{' '}
        <strong className="text-foreground">Dispatcher</strong> is a single control plane for carriers,
        labels, tracking and analytics — and it honours the delivery you promised the customer.
      </Lead>

      <H3>From paid to picked up</H3>
      <Steps>
        <Step title="Dispatch is drafted automatically">
          When the order hits <strong>Payment confirmed</strong>, a dispatch (status <Code>PENDING</Code>)
          and a shipping label (<Code>DRAFT</Code>) are created in the same step.
        </Step>
        <Step title="Assign the courier">
          The assignment honours the <strong>committed courier</strong> from checkout. If none was set
          (e.g. a manual order), it picks one by your routing rules.
        </Step>
        <Step title="Print & hand over">
          Generate and print the label, assign it to the partner, and the parcel is collected.
        </Step>
        <Step title="Track to the door">
          Carrier updates flow in automatically; the order moves through transit to delivered.
        </Step>
      </Steps>

      <H3>The Dispatcher’s tabs</H3>
      <DefList
        items={[
          { term: 'Tiers', def: 'Configure the delivery options/tiers customers can pick.' },
          { term: 'Partners', def: 'Manage your carrier connections.' },
          { term: 'Routing rules', def: 'Rule-based tier/partner assignment.' },
          { term: 'Quotes & audit', def: 'Delivery quotes with the full decision trace.' },
          { term: 'Dispatch', def: 'Live shipments — status, plus a “Moving / Late” lens.' },
          { term: 'Shipping Labels', def: 'Print and assign labels.' },
          { term: 'Analytics', def: 'Carrier scorecards and delivery insights.' },
        ]}
      />

      <H3>Shipment status</H3>
      <Lifecycle
        states={[
          { label: 'Pending pack', tone: 'muted' },
          { label: 'Awaiting pickup', tone: 'default' },
          { label: 'Picked', tone: 'default' },
          { label: 'In transit', tone: 'default' },
          { label: 'Out for delivery', tone: 'accent' },
          { label: 'Delivered', tone: 'success' },
        ]}
        branches={[{ from: 'in transit', to: [{ label: 'Dispatch failed', tone: 'danger' }, { label: 'Returned', tone: 'warn' }] }]}
      />

      <Callout tone="info" title="A promise you keep — or escalate">
        The committed courier and delivery date (EDD) are frozen when the customer checks out.
        Fulfilment <strong>honours that courier</strong>; if it genuinely can’t be booked, the dispatch
        escalates to an operator rather than silently downgrading the customer’s promise.
      </Callout>
    </div>
  );
}

// ── 7 · Returns ──────────────────────────────────────────────────────────────
function Returns() {
  return (
    <div>
      <Lead>
        A return moves a delivered item back through review, pickup, inspection and disposition — and
        settles the customer’s refund automatically once the goods are checked.
      </Lead>

      <H3>The return flow</H3>
      <Steps>
        <Step title="Review the request">
          Customer returns arrive in <GoTo to="/return-requests">Return Requests</GoTo> as{' '}
          <Code>REQUESTED</Code>. A reviewer approves or rejects (see <GoTo to="/my-reviews">My Reviews</GoTo>).
        </Step>
        <Step title="Auto reverse pickup">
          On approval, a reverse dispatch is created automatically to collect the goods.
        </Step>
        <Step title="Scan inward">
          When the parcel arrives, scan it in at the warehouse — received quantities are recorded in{' '}
          <GoTo to="/returns">Return Orders</GoTo>.
        </Step>
        <Step title="QC & disposition">
          Inspect each line and classify it <strong>good</strong> (back to sellable inventory) or{' '}
          <strong>bad</strong> (scrap/quarantine).
        </Step>
        <Step title="Refund & close">
          When QC completes, a refund is auto-created for the good units at their original price; the
          return closes.
        </Step>
      </Steps>

      <Lifecycle
        states={[
          { label: 'Requested', tone: 'warn' },
          { label: 'Approved', tone: 'accent' },
          { label: 'Received', tone: 'default' },
          { label: 'QC pending', tone: 'default' },
          { label: 'Good / Bad inv', tone: 'default' },
          { label: 'Closed', tone: 'success' },
        ]}
        branches={[{ from: 'Requested', to: [{ label: 'Rejected', tone: 'danger' }] }]}
      />

      <Callout tone="tip" title="Good stock comes back automatically">
        Items classified <strong>good</strong> in QC return to sellable inventory, so your stock stays
        accurate without a separate adjustment.
      </Callout>
    </div>
  );
}

// ── 8 · Cancellations & refunds ──────────────────────────────────────────────
function CancelsRefunds() {
  return (
    <div>
      <Lead>
        A cancellation stops an order before it ships and refunds the customer. Like returns, it’s a
        review-then-settle loop — and both feed the same refunds ledger.
      </Lead>

      <H3>Cancellations</H3>
      <P>
        Customer cancels land in <GoTo to="/cancel-requests">Cancel Requests</GoTo> as{' '}
        <Code>PENDING_REVIEW</Code>. A reviewer approves (a refund is created and the portal is
        notified) or rejects. When the portal’s gateway confirms the money is back, it closes as
        refunded.
      </P>
      <Lifecycle
        states={[
          { label: 'Pending review', tone: 'warn' },
          { label: 'Approved', tone: 'accent' },
          { label: 'Refunding', tone: 'default' },
          { label: 'Refunded', tone: 'success' },
        ]}
        branches={[{ from: 'review', to: [{ label: 'Rejected', tone: 'danger' }, { label: 'Refund failed', tone: 'danger' }] }]}
      />

      <H3>Refunds</H3>
      <P>
        Every approved cancel and QC’d return creates a <strong>refund</strong> in{' '}
        <GoTo to="/refunds">Refunds</GoTo>. Refunds can be <Code>FULL</Code>, <Code>PARTIAL</Code> or{' '}
        <Code>STORE_CREDIT</Code>, and move <Code>INITIATED</Code> → <Code>PROCESSED</Code> as the
        gateway settles (or <Code>FAILED</Code> to retry).
      </P>

      <H3>My Reviews — your personal queue</H3>
      <P>
        <GoTo to="/my-reviews">My Reviews</GoTo> is a single, oldest-first list of the cancels and
        returns assigned to you for a decision — so nothing waits past its SLA. Filter by Cancel or
        Return and clear them top-down.
      </P>

      <Callout tone="info" title="Refunds settle through your gateway">
        Zafroi initiates the refund and tracks it; your payment connector (e.g. Razorpay) actually
        moves the money and confirms back — closing the loop end to end.
      </Callout>
    </div>
  );
}

// ── 9 · Reference & go-live ──────────────────────────────────────────────────
function Reference() {
  return (
    <div>
      <Lead>The statuses across the sell-side, the gates to remember, and the traps to avoid.</Lead>

      <H3>Status glossary</H3>
      <DefList
        items={[
          { term: 'Order', def: <>Draft → Order placed → Stock reserved → Payment pending → Payment confirmed → Ready to dispatch → Dispatched → Delivered (or Cancelled).</> },
          { term: 'Payment', def: <><Code>PENDING / INITIATED / SUCCESS / FAILED</Code>.</> },
          { term: 'Invoice', def: <><Code>DRAFT / ISSUED / PAID / PARTIALLY_PAID / CANCELLED</Code>.</> },
          { term: 'Shipment', def: <>Pending pack → Awaiting pickup → Picked → In transit → Out for delivery → Delivered (or Dispatch failed / Returned).</> },
          { term: 'Return', def: <>Requested → Approved → Received → QC pending → Good/Bad inventory → Closed (or Rejected).</> },
          { term: 'Cancel', def: <>Pending review → Approved → Refunding → Refunded (or Rejected / Refund failed).</> },
          { term: 'Refund', def: <><Code>REQUESTED / APPROVED / INITIATED / PROCESSED / FAILED</Code>; type FULL / PARTIAL / STORE_CREDIT.</> },
        ]}
      />

      <H3>Remember the gates</H3>
      <UL>
        <LI><strong>Prepaid must be paid</strong> before invoice or dispatch; COD is settled on delivery.</LI>
        <LI><strong>Payment success auto-raises the invoice and drafts the dispatch</strong> — you rarely do those by hand.</LI>
        <LI><strong>The committed courier + EDD are honoured</strong>, or the dispatch escalates to you.</LI>
        <LI><strong>Approved cancels and QC’d returns auto-create refunds</strong> — settle them in Refunds.</LI>
      </UL>

      <H3>Common pitfalls</H3>
      <UL>
        <LI><strong>Trying to ship an unpaid prepaid order</strong> — record/confirm the payment first; the gate blocks it by design.</LI>
        <LI><strong>Wrong customer/address GSTIN</strong> → wrong CGST/SGST vs IGST. Check the shipping address GSTIN for inter-state orders.</LI>
        <LI><strong>Ignoring a dispatch escalation</strong> — if the committed courier can’t be booked, an operator must re-assign so the promise is kept.</LI>
        <LI><strong>Leaving QC unfinished</strong> — refunds only auto-create once every return line is classified.</LI>
      </UL>

      <Checklist
        items={[
          { label: 'Customer & address (with GSTIN) set', to: '/customers' },
          { label: 'Order placed & stock reserved', to: '/orders' },
          { label: 'Payment confirmed (prepaid) or COD', to: '/orders' },
          { label: 'Invoice issued', to: '/invoices' },
          { label: 'Dispatched on the committed courier', to: '/dispatch/dispatcher' },
          { label: 'After-sales queues clear', to: '/my-reviews' },
        ]}
      />

      <Callout tone="success" title="That’s the full loop">
        Customer → order → payment → invoice → dispatch → delivery → after-sales. Combined with{' '}
        <Link to="/guide/procurement" className="font-medium text-primary hover:underline">Procurement</Link>{' '}
        (materials in) and <Link to="/guide/portal" className="font-medium text-primary hover:underline">Portal Integration</Link>{' '}
        (your storefront), you now run the business end to end on Zafroi.
      </Callout>
    </div>
  );
}

// ── Chart ─────────────────────────────────────────────────────────────────────
function FunnelChart() {
  const data = [
    { stage: 'Placed', value: 100, fill: GRAY },
    { stage: 'Paid', value: 86, fill: TEAL },
    { stage: 'Dispatched', value: 80, fill: TEAL },
    { stage: 'Delivered', value: 74, fill: ORANGE },
  ];
  return (
    <div className="h-[180px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 0, right: 28, left: 8, bottom: 0 }}>
          <XAxis type="number" hide />
          <YAxis type="category" dataKey="stage" width={78} tickLine={false} axisLine={false} fontSize={12} stroke="hsl(220 9% 50%)" />
          <Tooltip
            cursor={{ fill: 'hsl(220 13% 91% / 0.4)' }}
            contentStyle={{ borderRadius: 8, border: '1px solid hsl(220 13% 91%)', fontSize: 12 }}
            formatter={(v: number) => [`${v} orders`, '']}
          />
          <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={22}>
            {data.map((d, i) => (
              <Cell key={i} fill={d.fill} />
            ))}
            <LabelList dataKey="value" position="right" fontSize={11} fill="hsl(220 9% 46%)" />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Chapters export ──────────────────────────────────────────────────────────
export const salesChapters: GuideChapter[] = [
  { id: 'overview', title: 'Overview', icon: ShoppingCart, render: Overview },
  { id: 'customers', title: 'Customers', icon: UserSquare, render: Customers },
  { id: 'orders', title: 'Sales orders', icon: ClipboardList, render: Orders },
  { id: 'payments', title: 'Payment & gating', icon: CreditCard, render: Payments },
  { id: 'invoicing', title: 'Invoicing', icon: ReceiptText, render: Invoicing },
  { id: 'dispatch', title: 'Dispatch & delivery', icon: BrainCircuit, render: Dispatch },
  { id: 'returns', title: 'Returns', icon: Undo2, render: Returns },
  { id: 'cancels-refunds', title: 'Cancellations & refunds', icon: RotateCcw, render: CancelsRefunds },
  { id: 'reference', title: 'Reference & go-live', icon: BookMarked, render: Reference },
];
